import { useState, useEffect } from 'react';
import Overlay from '../Overlay';
import InlineMessage from '../InlineMessage';
import useMessage from '../../lib/useMessage';
import { getCompanyName } from '../../lib/tenant';
import {
  countForExport,
  fetchAllForExport,
  applySearch,
  buildMovementTable,
  buildValuationTable,
  describeExport,
  toCsv,
  logExport,
  EXPORT_HARD_CAP,
} from '../../lib/ledgerExport';

// Issue #394, Session 78n7. One export control that asks a single question and
// tells the truth about what it is about to hand over.
//
// It replaces two silent buttons -- CSV and PDF -- that each re-ran the query
// with no row limit at all, so the server's own ceiling applied and the file
// came out well-formed, professional and incomplete, with a row count on its
// face that agreed with the truncated set.
//
// Approved gate: docs/gates/Stede_DocumentOutput_Gate_395.html, screen 1.

export default function LedgerExportSheet({
  isMobile,
  onClose,
  // everything the export needs, passed in rather than re-derived
  filters, start, end, canSeeAllBranches, userBranchId, canSeeCost,
  branchLabel, dateLabel, txTypeLabel, userLabel,
  branchName, txLabel, fmtDate,
  stats, profile,
}) {
  const [layout, setLayout] = useState('movement');
  const [format, setFormat] = useState('csv');
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(true);
  const [working, setWorking] = useState(false);
  const msg = useMessage();

  const fetchOpts = { filters, start, end, canSeeAllBranches, userBranchId, withCost: canSeeCost };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCounting(true);
      try {
        const n = await countForExport(fetchOpts);
        if (!cancelled) setCount(n);
      } catch (e) {
        if (!cancelled) msg.showError('Could not count the matching transactions: ' + (e.message || e));
      } finally {
        if (!cancelled) setCounting(false);
      }
    })();
    return () => { cancelled = true; };
    // Counting depends on the filters, which do not change while this is open.
  }, []); // eslint-disable-line

  const tooLarge = count != null && count > EXPORT_HARD_CAP;

  async function doExport() {
    setWorking(true);
    msg.clearMessage();
    try {
      const raw = await fetchAllForExport(fetchOpts, count);
      // The search box is applied here rather than by the server. Before this
      // session that was a real defect on top of the truncation -- a capped
      // export searched only the first thousand rows. Now the full set is in
      // hand, so this filters everything the filters matched.
      const rows = applySearch(raw, filters.search);

      const table = layout === 'valuation'
        ? buildValuationTable(rows, { withCost: canSeeCost })
        : buildMovementTable(rows, { branchName, txLabel, withCost: canSeeCost, fmtDate });

      const block = describeExport({
        companyName: getCompanyName(),
        layout,
        branchLabel,
        dateLabel,
        txLabel: txTypeLabel,
        userLabel,
        search: filters.search,
        // The count the FILE states is the count actually written, not the
        // pre-search estimate the dialog showed. Those differ when a search is
        // active, and the file must describe itself accurately.
        rowCount: layout === 'valuation' ? table.body.length : rows.length,
        withCost: canSeeCost,
        canSeeCostAtAll: canSeeCost,
        generatedBy: profile?.full_name || 'Unknown',
        generatedAt: new Date().toLocaleString('en-US'),
      });

      if (format === 'csv') {
        downloadCsv(block, table);
      } else {
        await downloadPdf(block, table);
      }

      await logExport({
        profile,
        layout,
        format,
        filters: {
          branch: branchLabel, date_range: dateLabel,
          tx_type: filters.txType || null, user: filters.userId || null,
          search: filters.search || null,
        },
        rowCount: layout === 'valuation' ? table.body.length : rows.length,
        includedCost: canSeeCost,
      });

      onClose();
    } catch (e) {
      msg.showError('The export could not be completed: ' + (e.message || e));
      setWorking(false);
    }
  }

  function fileStem() {
    const b = (branchLabel || 'all').toLowerCase().replace(/\s+/g, '_');
    return `${layout === 'valuation' ? 'valuation' : 'transactions'}_${b}_${filters.dateRange}`;
  }

  function downloadCsv(block, table) {
    const blob = new Blob([toCsv(block, table.headers, table.body)], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileStem()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf(block, table) {
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.width;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(layout === 'valuation' ? 'Inventory Valuation' : 'Transaction Ledger', 40, 36);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    const rowsWord = layout === 'valuation' ? 'items' : 'transactions';
    doc.text(
      `${getCompanyName()} · ${branchLabel} · ${dateLabel} · ${table.body.length} ${rowsWord}`,
      40, 52
    );
    doc.text(
      `Generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      W - 40, 52, { align: 'right' }
    );
    doc.setDrawColor(15, 110, 86);
    doc.setLineWidth(1.5);
    doc.line(40, 58, W - 40, 58);

    let startY = 76;
    if (layout === 'movement' && stats) {
      const labels = ['Received', 'Consumed', 'Net change'];
      const vals = [
        `+${stats.received}`,
        `-${stats.consumed}`,
        stats.net >= 0 ? `+${stats.net}` : `-${Math.abs(stats.net)}`,
      ];
      const colors = [[15, 110, 86], [163, 45, 45], [12, 68, 124]];
      labels.forEach((label, i) => {
        const x = 40 + i * 178;
        doc.setFillColor(245, 245, 243);
        doc.roundedRect(x, 66, 168, 36, 3, 3, 'F');
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(130, 130, 130);
        doc.text(label.toUpperCase(), x + 10, 78);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors[i]);
        doc.text(vals[i], x + 10, 94);
      });
      startY = 112;
    }

    // The provenance block the CSV carries is printed on the PDF too, so the
    // two files say the same thing about themselves. Gate D12.
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(140, 140, 140);
    const facts = block
      .filter(r => r.length === 2)
      .map(([k, v]) => `${k}: ${v}`)
      .join('   ·   ');
    const wrapped = doc.splitTextToSize(facts, W - 80);
    doc.text(wrapped, 40, startY);
    startY += wrapped.length * 10 + 8;

    autoTable(doc, {
      head: [table.headers],
      body: table.body,
      startY,
      styles: { fontSize: 7.5, cellPadding: 4, font: 'helvetica' },
      headStyles: { fillColor: [15, 110, 86], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 249, 248] },
    });

    // "Page N of M", issue item 5. The old footer printed `Page N` with no
    // total, so a reader holding a printout could not tell whether pages were
    // missing -- which defeats the purpose of paginating a document handed to
    // an accountant. The total is only knowable after the table is laid out,
    // so the footer is written in a second pass over the finished pages.
    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(160, 160, 160);
      const H = doc.internal.pageSize.height;
      doc.text(`${getCompanyName()} · ${branchLabel} · ${dateLabel}`, 40, H - 20);
      doc.text(`Page ${p} of ${pageCount}`, W - 40, H - 20, { align: 'right' });
    }

    doc.save(`${fileStem()}.pdf`);
  }

  const pill = (active) => ({
    flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
    border: active ? '1.5px solid #1D9E75' : '1px solid var(--gray-ln, #E3E3DF)',
    background: active ? '#EAF7F2' : '#fff',
    fontWeight: active ? 700 : 500,
    color: 'var(--text-primary)',
  });

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={working ? undefined : onClose} maxWidth={520}>
      <div className="modal-header">
        <span className="modal-title">Export ledger</span>
        <button className="close-btn" onClick={onClose} disabled={working}>×</button>
      </div>

      <div className="modal-body">
        <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} />

        <div className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          {branchLabel} · {dateLabel}{filters.txType ? ` · ${txTypeLabel}` : ''}
        </div>

        {/* Gate callout D -- the question is what you NEED, not which report
            exists. NetSuite, Fishbowl and SAP all make you know the report's
            name and go find it in a menu. */}
        <div className="form-label" style={{ marginBottom: 6 }}>What do you need?</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <button style={pill(layout === 'movement')} onClick={() => setLayout('movement')}>
            Movement detail
          </button>
          <button style={pill(layout === 'valuation')} onClick={() => setLayout('valuation')}>
            Valuation
          </button>
        </div>
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.45 }}>
          {layout === 'movement'
            ? 'Every transaction, one row each — who moved what, when, and what it cost.'
            : 'One row per item — what it opened at, what moved, and what it closed at.'}
        </div>

        <div className="form-label" style={{ marginBottom: 6 }}>Format</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button style={pill(format === 'csv')} onClick={() => setFormat('csv')}>CSV</button>
          <button style={pill(format === 'pdf')} onClick={() => setFormat('pdf')}>PDF</button>
        </div>

        {/* Gate callout E -- the row count is stated BEFORE the file is made,
            not printed on it afterwards. Printed afterwards it agreed with the
            truncated set, which is what made the failure invisible. */}
        <div style={{
          padding: '11px 13px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
          background: tooLarge ? '#FDF3F3' : 'var(--off-white, #F5F5F3)',
          border: `1px solid ${tooLarge ? '#F0CFCF' : 'var(--gray-ln, #E3E3DF)'}`,
          marginBottom: 14,
        }}>
          {counting ? 'Counting what matches your filters…'
            : count == null ? 'The matching rows could not be counted.'
            : tooLarge ? (
              <>
                <strong>{count.toLocaleString()} transactions match your filters</strong> — more than
                one file can carry. Narrow the date range or the branch, then export again.
              </>
            ) : (
              <>
                <strong>{count.toLocaleString()}</strong> {count === 1 ? 'transaction matches' : 'transactions match'} your
                filters. {count === 1 ? 'It' : `All ${count.toLocaleString()}`} will be included
                {layout === 'valuation' ? ', grouped into one row per item' : ''}.
                {filters.search ? ' The search box is applied to the whole set, then stated in the file.' : ''}
              </>
            )}
        </div>

        {/* Gate callout F -- a missing cost column says WHY it is missing.
            "Not available to you" is a permission; a blank column is a bug, and
            the reader cannot tell them apart. */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: 12,
          padding: '9px 13px', fontSize: 13,
          border: '1px solid var(--gray-ln, #E3E3DF)', borderRadius: 8,
        }}>
          <span>Unit cost and extended value</span>
          <span style={{ fontWeight: canSeeCost ? 700 : 400 }} className={canSeeCost ? '' : 'text-muted'}>
            {canSeeCost ? 'Included' : 'Not available to you'}
          </span>
        </div>
        {!canSeeCost && (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
            The file says so too, in its header block — so a reader knows the omission was a
            permission, not an empty column.
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose} disabled={working}>Cancel</button>
        {/* Gate callout G -- the button is BLOCKED, not refused after the click.
            An error banner renders hundreds of pixels above the control that
            triggered it and reads as a broken button. */}
        <button
          className="btn btn-primary"
          onClick={doExport}
          disabled={working || counting || tooLarge || count == null || count === 0}
        >
          {working ? 'Exporting…' : `Export ${format.toUpperCase()}`}
        </button>
      </div>
    </Overlay>
  );
}
